import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { translateContent, type TranslatableFields } from "@/lib/contentTranslation.functions";
import { useLocale } from "@/i18n/useLocale";

/**
 * Show a piece of content in the reader's language. English readers get the
 * original straight away; other languages fall back to the original text
 * until the translation arrives, so nothing ever renders blank.
 */
export function useContentTranslation(
  contentType: string,
  contentId: string | null | undefined,
  fields: TranslatableFields,
): { text: (key: string) => string; ready: boolean } {
  const locale = useLocale();
  const run = useServerFn(translateContent);
  const [translated, setTranslated] = useState<TranslatableFields | null>(null);
  const requested = useRef<string | null>(null);

  const signature = useMemo(() => Object.keys(fields).sort().join("|"), [fields]);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  useEffect(() => {
    if (locale === "en" || !contentId) return;
    const key = `${contentType}:${contentId}:${locale}:${signature}`;
    if (requested.current === key) return;
    requested.current = key;
    setTranslated(null);

    let active = true;
    (async () => {
      try {
        const result = await run({
          data: {
            contentType,
            contentId,
            languageCode: locale,
            fields: fieldsRef.current,
          },
        });
        if (active && result?.success && result.fields) setTranslated(result.fields);
      } catch {
        // Keep the original wording when translation is unavailable.
      }
    })();
    return () => {
      active = false;
    };
  }, [contentType, contentId, locale, signature, run]);

  return {
    ready: locale === "en" || translated !== null,
    text: (key: string) => translated?.[key] ?? fieldsRef.current[key] ?? "",
  };
}
